import { useState } from "react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Check, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/api";

const PackageCard = ({ pkg, index }) => {
  const [spot, setSpot] = useState({ x: 50, y: 50 });
  const [hover, setHover] = useState(false);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setSpot({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    });
  };

  return (
    <Reveal delay={index * 0.1}>
      <div
        data-testid={`package-card-${pkg.id}`}
        onMouseMove={onMove}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border p-8 transition-all duration-500 ease-out hover:-translate-y-2 ${
          pkg.highlight
            ? "glow-card border-[#00F0FF]/50 bg-[#0A0F1C] hover:shadow-[0_24px_80px_-20px_rgba(0,240,255,0.45)]"
            : "border-white/10 bg-[#0A0F1C] hover:border-[#00F0FF]/40 hover:shadow-[0_24px_70px_-24px_rgba(0,240,255,0.3)]"
        }`}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 transition-opacity duration-500"
          style={{
            opacity: hover ? 1 : 0,
            background: `radial-gradient(340px circle at ${spot.x}% ${spot.y}%, rgba(0,240,255,0.13), transparent 65%)`,
          }}
        />
        {pkg.highlight && (
          <span className="absolute -top-0 right-8 rounded-b-xl bg-[#00F0FF] px-4 py-1.5 font-display text-xs font-bold uppercase tracking-widest text-[#050811]">
            Most Popular
          </span>
        )}
        <h3 className="relative font-display text-xl font-bold text-white transition-colors duration-300 group-hover:text-[#00F0FF]">
          {pkg.name}
        </h3>
        <p className="relative mt-2 min-h-[3rem] text-sm text-slate-400">{pkg.tagline}</p>
        <div className="relative mt-6 flex items-baseline gap-2">
          <span
            className="font-display text-4xl font-black tracking-tight text-white transition-transform duration-500 ease-out group-hover:scale-110 group-hover:text-[#00F0FF] group-hover:[text-shadow:0_0_30px_rgba(0,240,255,0.4)] origin-left"
            data-testid={`package-price-${pkg.id}`}
          >
            {pkg.price}
          </span>
          <span className="text-sm text-slate-500">{pkg.period}</span>
        </div>
        <ul className="relative mt-8 flex-1 space-y-3.5">
          {pkg.features.map((f, i) => (
            <li
              key={f}
              className="flex items-start gap-3 text-sm text-slate-300 transition-transform duration-300 ease-out group-hover:translate-x-1"
              style={{ transitionDelay: hover ? `${i * 35}ms` : "0ms" }}
            >
              <Check size={17} className="mt-0.5 shrink-0 text-[#00F0FF] transition-transform duration-300 group-hover:scale-110" />
              {f}
            </li>
          ))}
        </ul>
        <p className="relative mt-6 border-t border-white/10 pt-4 text-xs text-slate-500">
          {pkg.maintenance}
        </p>
        <button
          data-testid={`package-cta-${pkg.id}`}
          onClick={() => scrollToId("contact")}
          className={`relative mt-6 flex items-center justify-center gap-2 overflow-hidden rounded-full px-6 py-3 text-sm font-bold transition-all duration-300 ${
            pkg.highlight
              ? "bg-[#00F0FF] text-[#050811] hover:shadow-[0_0_32px_rgba(0,240,255,0.6)]"
              : "border border-white/20 text-white group-hover:border-[#00F0FF]/70 group-hover:bg-[#00F0FF]/10 group-hover:text-[#00F0FF]"
          }`}
        >
          {pkg.cta || `Start with ${pkg.name}`}
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
        </button>
      </div>
    </Reveal>
  );
};

export default function Packages({ packages }) {
  return (
    <section id="packages" className="py-24 lg:py-32" data-testid="packages-section">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal className="mx-auto max-w-3xl text-center">
          <Eyebrow>Our Packages</Eyebrow>
          <h2 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
            Clear packages. Honest pricing.
          </h2>
          <p className="mt-5 text-base text-slate-400 sm:text-lg">
            Every package is a starting point — your final quote is confirmed in writing after a
            free consultation, so you always know exactly what you're paying for.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {packages.map((pkg, i) => (
            <PackageCard key={pkg.id} pkg={pkg} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
